/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage/index
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/



import React, { useState, useEffect } from "react";
import { db } from "../../../../../backend/firebaseConfig.js";
import { doc, onSnapshot, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { servicesList as defaultServicesList } from "../../../organisms/list/ServicesList.jsx";

// Imports MUI Components
import {
  Box,
  Grid,
  Stack,
  Card,
  CardMedia,
  CardContent,
  Rating,
  Typography
} from "@mui/material"

import Text from "../../../atoms/text/Text"

import servicesGeneral from "../../../../../assets/services_general.png"
import servicesSurgery from "../../../../../assets/services_surgery.png"
import servicesRepair from "../../../../../assets/services_urgent.png"
import servicesDenture from "../../../../../assets/icons/denture.png"

// Import Functions
import { fetchUserDetails } from "../../../../functions/fetchUserDetails.jsx";
import { notifyFirstUser } from "../../../../functions/notifyFirstUser";
import ButtonComp from "../../../atoms/button/Button.jsx";
import Icon from "../../../atoms/icon/Icon.jsx";

// import "./styles.css"

const Section = ({
  section,
}) => {

  const [services, setServices] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingService, setEditingService] = useState(null);
  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);

  const auth = getAuth();
  const navigate = useNavigate();

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    const notify = notifyFirstUser();
    return () => {
      details && details();
      notify && notify();
    };
  }, []);

  // Fetch user details for sidebar profile
  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          setUserDetails(snapshot.data());
        }
      }
    });
    return () => {
      authUnsubscribe();
    };
  }, [auth]);

  // Load services from ServicesList component
  useEffect(() => {
    const loadServices = async () => {
        const servicesRef = doc(db, "services", "servicesList");
        const snapshot = await getDoc(servicesRef);
        
        if (snapshot.exists()) {
          setServices(snapshot.data().servicesList || []);
        } else {
          // If no services in Firestore, use default from ServicesList
          setServices(defaultServicesList);
          // Save default services to Firestore
          await setDoc(servicesRef, { servicesList: defaultServicesList });
        }
    };
    
    loadServices();
  }, []);


  // Filter services based on search term
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if user is clinic personnel
  const isClinicPersonnel = userDetails?.role === "DentistOwner" || 
                            userDetails?.role === "AssociateDentist" || 
                            userDetails?.role === "ClinicStaff";

  // Handle Schedule Appointment button click
  const handleScheduleAppointment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsScheduleLoading(true);
    
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      // Not signed in - redirect to sign-in
      setIsScheduleLoading(false);
      navigate("/sign-in", { 
        state: { 
          from: "/patient-appointment-booking",
          message: "Please sign in to schedule an appointment"
        } 
      });
      return;
    }
    
    // Check if user is clinic personnel
    // First check userDetails, if not available then check Firestore
    let userRole = userDetails?.role;
    if (!userRole) {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          userRole = userSnap.data().role || userSnap.data().userType;
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    }
    
    // If clinic personnel, navigate to manage appointment page
    if (userRole === "DentistOwner" || userRole === "AssociateDentist" || userRole === "ClinicStaff") {
      setIsScheduleLoading(false);
      navigate("/manage-appointment");
      return;
    }
    
    // Check email verification status before navigating
    try {
      // Check Firebase Auth email verification (source of truth)
      if (!currentUser.emailVerified) {
        // Email not verified - sign out and redirect to sign-in
        setIsScheduleLoading(false);
        try {
          await signOut(auth);
        } catch (error) {
          console.error("Error signing out:", error);
        }
        navigate("/sign-in", {
          state: {
            from: "/patient-appointment-booking",
            message: "Please verify your email address before scheduling an appointment. Check your inbox for the verification link."
          }
        });
        return;
      }
      
      // Also check Firestore for mobile-only users
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const isMobileOnly = userData.emailVerified === "MobileOnly";
        const emailVerified = userData.emailVerified === "Yes";
        const isMobileEmail = currentUser.email?.includes("@mobileuser.dentalapp.com");
        
        // If mobile-only or email is verified, proceed to booking
        if (!isMobileOnly && !emailVerified && !isMobileEmail) {
          // Email not verified in Firestore either - sign out and redirect to sign-in
          setIsScheduleLoading(false);
          try {
            await signOut(auth);
          } catch (error) {
            console.error("Error signing out:", error);
          }
          navigate("/sign-in", {
            state: {
              from: "/patient-appointment-booking",
              message: "Please verify your email address before scheduling an appointment. Check your inbox for the verification link."
            }
          });
          return;
        }
      }
      
      // Email is verified - navigate to appointment booking
      setIsScheduleLoading(false);
      navigate("/patient-appointment-booking");
    } catch (error) {
      console.error("Error checking email verification:", error);
      // On error, proceed to booking and let ProtectedRoute handle it
      setIsScheduleLoading(false);
      navigate("/patient-appointment-booking");
    }
  };

  const gridStylesWrapper = {
    backgroundColor: "transparent",
    padding: {
      xs: "80px 20px 80px 20px", 
      sm: "80px 30px 80px 30px", 
      md: "80px 110px 80px 110px", 
      lg: "80px 110px 80px 110px", 
      xl: "80px 110px 80px 110px" 
    },
    width: "100%",
    "& .MuiGrid-item": {
      display: "flex",
      justifyContent: "center",
    },
  }

  const gridStylesContainer = {
    padding: "30px 0 80px 0",
    width: "100%",
    "& .MuiGrid-item": {
      display: "flex",
      justifyContent: "center",
    },
  }

  const boxStylesWrapper = {
    width: "100%",
    display: "flex",
    justifyContent: "space-evenly",
    flexDirection: "column",
  }

  const cardStyleContainer = {
    padding: "20px",
    borderRadius: "20px",
    backgroundColor: "#FEFEFE",
    height: "80%"
  }

  const boxStylesTitle = {
    padding: "15px 0",
    borderBottom: "2px solid rgba(89, 84, 74, .2)"
  }

  const stackStylesCost = {
    justifyContent: "space-between",
    padding: "15px 0",
  }

  const typographyStylesH3 = {
    color: "#59544A",
    fontWeight: "300",
    padding: "0px 10px",
  }

  return (
    <>
      {/*Grids */}
      <Grid
        container
        rowSpacing={4}
        columnSpacing={6}
        sx={gridStylesWrapper}
      >
  
        {/* Services List */}
        <Grid 
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12,
            xl: 12
          }} 
          item
        >
            
{/* Group services by category */}
{Object.entries(
  filteredServices.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {})
).map(([category, categoryServices]) => {
  // Get the categoryDef from the first service that has it
  const categoryDef =
    categoryServices.find((service) => service.categoryDef)?.categoryDef || "";

  return (
    <div key={category}>
      {/* Title */}
      <Box sx={boxStylesWrapper}>
        {/* Icon & Title Wrapper */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "row",
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              justifyContent: "center",
              alignItems: "center",
              margin: "auto 0",
            }}
          >
            <Icon
              src={
                category === "Basic Services" ? servicesGeneral :
                category === "Denture" ? servicesDenture :
                category === "Denture Repair" ? servicesRepair :
                servicesSurgery
              }
              size="large"
            />
          </Box>

          {/* Title + categoryDef */}
          <Box
            sx={{
              width: "100%",
              margin: "20px auto",
              display: "flex",
              justifyContent: "space-evenly",
              flexDirection: "column",
              // textAlign: "justify"
            }}
          >
            <Stack direction={"column"} spacing={1}>
              <Text text={category} variant="h2" />

              {/* This now comes from Firestore (categoryDef field) */}
              <Typography variant="h3" sx={typographyStylesH3}>
                {categoryDef}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      <Grid
        container
        rowSpacing={2}
        columnSpacing={5}
        sx={gridStylesContainer}
      >
        {categoryServices.map((service) => (
          <Grid
            key={service.name}
            size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
            item
          >
              <Card elevation={5} sx={cardStyleContainer}>
                <CardContent sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  height: "100%"
                }}>

                  <Box>
                    <Text text={service.name} variant="h3" />
                  </Box>

                  <Box sx={boxStylesTitle}>
                    <Text text={service.definition} variant="body1" />
                  </Box>

                  <Box>
                    {/* <Stack direction="row" sx={stackStylesCost}>
                      <Text text="Estimated time ~ 30 mins" variant="body1" />
                    </Stack> */}
                  </Box>

                  <Box sx={{ 
                    height: "100%",
                    alignContent: "end",
                    width: "100%",
                    mb: "20px",
                    alignItems: "end", 
                    justifyContent: "end",
                  }}>

                    <Box sx={{mt: "auto"}}>
                    <ButtonComp
                      variant="contained"
                      noElevate={true}
                      noRipple={true}
                      label={
                        isScheduleLoading ? 
                          userDetails?.role === "DentistOwner" ? "Manage Services..." : 
                          userDetails?.role === "AssociateDentist" || 
                          userDetails?.role === "ClinicStaff" ? "Manage Appointment..."
                            : "Schedule Appointment..."
                          : 
                          userDetails?.role === "DentistOwner" ? "Manage Services" : 
                          userDetails?.role === "AssociateDentist" || 
                          userDetails?.role === "ClinicStaff" ? "Manage Appointment"
                            : "Schedule Appointment"
                      }
                      color="light"
                      paddings="default"
                      background="default"
                      font="default"
                      weight="default"
                      border="default"
                      transform="default"
                      selected={true}
                      keys={false}
                      hoverTextColor="default"
                      hoverBackground="default"
                      hoverBorder="default"
                      noRead={isScheduleLoading}
                      type="default"
                      justify="default"
                      destination={userDetails?.role === "DentistOwner" ?"/manage-services" : userDetails?.role === "ClinicStaff" ?"/manage-appointment" : "/patient-appointment-booking"}
                      buttonId="scheduleAppointment"
                      open={true}
                    />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
})}


        </Grid>
          
      </Grid>
    </>
  )

}

export default Section;